import * as ng from 'angular';
import * as Linq from 'linq-es5';
import * as ASM from '../data/interfaces';
import { ISettingsResource } from '../data';

let ThemesServiceFactory: Function = function ($q: ng.IQService,
    $window: ng.IWindowService,
    $mdTheming:any,
    $log: ng.ILogService,
    themeProvider: ng.material.IThemingProvider,
    Settings: ISettingsResource): IThemesService {
    return new ThemesService($q, $window, $mdTheming, $log, themeProvider, Settings);
}

ThemesServiceFactory.$inject = ['$q', '$window', '$mdTheming', '$log', 'themeProvider', 'Settings'];

let Themes = {
    name: "Themes",
    factory: ThemesServiceFactory
};

interface IThemesService {
    loadThemes(): ng.IPromise<boolean>;
}

export class ThemesService implements IThemesService {
    constructor(private $q: ng.IQService,
        private $window:ng.IWindowService,
        private $mdTheming:any,
        private $log: ng.ILogService,
        private themeProvider: ng.material.IThemingProvider,
        private Settings: ISettingsResource) {
    }

    public loadThemes(): ng.IPromise<boolean> {

        return this.getThemeSettings()
            .then((themeSettings) => {

                let useBackupTheme = true;
                try {
                    if (!themeSettings) {
                        this.$log.log('Unable to load theme from server');
                    }
                    else {
                        if (themeSettings.customPalettes) {
                            themeSettings.customPalettes.forEach((x: any) => {
                                this.themeProvider.definePalette(x.name, x.settings);
                                this.sanitizePalette(x.settings, x.name);
                            });
                        }
                       
                        if (themeSettings.themes) {
                            themeSettings.themes.forEach((x) => {
                                if (x.name && x.primaryPalette && x.primaryPalette.paletteName) {
                                    let theme = this.themeProvider.theme(x.name)
                                        .primaryPalette(x.primaryPalette.paletteName, x.primaryPalette.overrideMap);
                                    if (x.accentPalette && x.accentPalette.paletteName) {
                                        theme.accentPalette(x.accentPalette.paletteName, x.accentPalette.overrideMap);
                                    }
                                    this.$mdTheming.generateTheme(x.name);
                                }
                            });

                            if (Linq.From(themeSettings.themes).Any(x => x.name == themeSettings.defaultThemeName)) {
                                this.themeProvider.setDefaultTheme(themeSettings.defaultThemeName);
                                useBackupTheme = false;
                                return this.$q.resolve(true);
                            }
                            else {
                                this.$log.error(`Default theme of name '${themeSettings.defaultThemeName}' does not exist in themes collection.`)
                            };
                        }
                    }
                }
                catch (e) {
                    this.$log.error(e);                    
                }
                finally {
                    if (useBackupTheme) {
                        //if the backup theam is being used then the client auth 
                        //token failed to be renewed, just reload the app and it will reset.
                        this.$window.location.reload();
                        this.$log.log('No themes loaded.');
                        //this.$log.log('No themes supplied. Using a temporary theme.');                     
                        //this.themeProvider.theme('temporary')
                        //    .primaryPalette('orange')
                        //    .accentPalette('green');
                        //this.$mdTheming.generateTheme('temporary');
                        //this.themeProvider.setDefaultTheme('temporary');
                        return this.$q.resolve(false);
                    }
                }

            });

    }

    private sanitizePalette(palette, name) {
        var DARK_CONTRAST_COLOR = this.colorToRgbaArray('rgba(0,0,0,0.87)');
        var LIGHT_CONTRAST_COLOR = this.colorToRgbaArray('rgba(255,255,255,0.87)');
        var STRONG_LIGHT_CONTRAST_COLOR = this.colorToRgbaArray('rgb(255,255,255)');


        var defaultContrast = palette.contrastDefaultColor;
        var lightColors = palette.contrastLightColors || [];
        var strongLightColors = palette.contrastStrongLightColors || [];
        var darkColors = palette.contrastDarkColors || [];

        // These colors are provided as space-separated lists
        if (typeof lightColors === 'string') lightColors = lightColors.split(' ');
        if (typeof strongLightColors === 'string') strongLightColors = strongLightColors.split(' ');
        if (typeof darkColors === 'string') darkColors = darkColors.split(' ');

        // Cleanup after ourselves
        delete palette.contrastDefaultColor;
        delete palette.contrastLightColors;
        delete palette.contrastStrongLightColors;
        delete palette.contrastDarkColors;

        // Change { 'A100': '#fffeee' } to { 'A100': { value: '#fffeee', contrast:DARK_CONTRAST_COLOR }
        ng.forEach(palette, (hueValue, hueName) => {
            if (ng.isObject(hueValue)) return; // Already converted
            // Map everything to rgb colors
            var rgbValue = this.colorToRgbaArray(hueValue);
            if (!rgbValue) {
                throw new Error("Color %1, in palette %2's hue %3, is invalid. Hex or rgb(a) color expected."
                    .replace('%1', hueValue)
                    .replace('%2', palette.name)
                    .replace('%3', hueName));
            }

            palette[hueName] = {
                hex: palette[hueName],
                value: rgbValue,
                contrast: getContrastColor()
            };
            function getContrastColor() {
                if (defaultContrast === 'light') {
                    if (darkColors.indexOf(hueName) > -1) {
                        return DARK_CONTRAST_COLOR;
                    } else {
                        return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
                            : LIGHT_CONTRAST_COLOR;
                    }
                } else {
                    if (lightColors.indexOf(hueName) > -1) {
                        return strongLightColors.indexOf(hueName) > -1 ? STRONG_LIGHT_CONTRAST_COLOR
                            : LIGHT_CONTRAST_COLOR;
                    } else {
                        return DARK_CONTRAST_COLOR;
                    }
                }
            }
        });
    }

    private colorToRgbaArray(clr) {
        if (ng.isArray(clr) && clr.length == 3) return clr;
        if (/^rgb/.test(clr)) {
            return clr.replace(/(^\s*rgba?\(|\)\s*$)/g, '').split(',').map(function (value, i) {
                return i == 3 ? (Number as any).parseFloat(value, 10) : parseInt(value, 10);
            });
        }
        if (clr.charAt(0) == '#') clr = clr.substring(1);
        if (!/^([a-fA-F0-9]{3}){1,2}$/g.test(clr)) return;

        var dig = clr.length / 3;
        var red = clr.substr(0, dig);
        var grn = clr.substr(dig, dig);
        var blu = clr.substr(dig * 2);
        if (dig === 1) {
            red += red;
            grn += grn;
            blu += blu;
        }
        return [parseInt(red, 16), parseInt(grn, 16), parseInt(blu, 16)];
    }

    private getThemeSettings(): ng.IPromise<ASM.IThemeSettings> {

        return this.Settings.get({ key: 'SiteTheme' }).$promise
            .then((response) => {
                let themeSettings = (response as ASM.ISettings<ASM.IThemeSettings>)
                    .settings as ASM.IThemeSettings;
                themeSettings.customPalettes.forEach(x => {
                    let replaceFields = ['100', '200', '400', '700']
                    replaceFields.forEach(y => {
                        var temp = x.settings['a' + y];
                        if (temp) {
                            x.settings['A' + y] = temp;
                            delete x.settings['a' + y];
                        }
                    });                    
                });
                return this.$q.resolve(themeSettings);
            })
            .catch((e) => {
                this.$log.log(e, 'SettingsService: Failed load of Settings for current user');
                return this.$q.resolve(e);
            });
    }

}

export { Themes, IThemesService };
export default Themes;


// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/services/themes-service.ts